import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import {
  RefreshCw, Search, CalendarRange, Download, AlertTriangle, Info,
  ChevronLeft, ChevronRight, Wallet, Boxes
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';
const PAGE_SIZE = 50;

const fmtNumber = (n) => Number(n || 0).toLocaleString('tr-TR');
const fmtMoney = (n) => `${Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺`;
// Hücreler dar — 12.345 → "12,3B" gibi kısaltılır, tam değer tooltip'te
const fmtCompact = (n) => {
  const v = Number(n || 0);
  if (v === 0) return '·';
  if (v >= 1000000) return `${(v / 1000000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M`;
  if (v >= 1000) return `${(v / 1000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}B`;
  return String(Math.round(v));
};
const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return d; }
};
const colLabel = (key) => {
  try {
    return new Date(`${key}T00:00:00`).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
  } catch { return key; }
};

const RANGES = [
  { key: '30', label: 'Son 30 gün' },
  { key: '60', label: 'Son 60 gün' },
  { key: '90', label: 'Son 90 gün' },
  { key: 'all', label: 'Tüm dönem' },
];
const ROLLS = [
  { key: 7, label: 'Stok: 7 günlük pencere' },
  { key: 14, label: 'Stok: 14 günlük pencere' },
  { key: 21, label: 'Stok: 21 günlük pencere' },
  { key: 30, label: 'Stok: 30 günlük pencere' },
];
// Hücrede ciro da beden de HER ZAMAN yazar; bu seçim sadece ısı haritasının
// hangi metriğe göre renklendiğini belirler.
const METRICS = [
  { key: 'revenue', label: 'Ciroya göre renklendir', icon: Wallet, hint: 'koyu = çok ciro' },
  { key: 'stock', label: 'Bedene göre renklendir', icon: Boxes, hint: 'koyu = çok beden' },
];
const SORTS = [
  { key: 'revenue_desc', label: 'Ciro (çoktan aza)' },
  { key: 'revenue_asc', label: 'Ciro (azdan çoğa)' },
  { key: 'stock_desc', label: 'Stok (çoktan aza)' },
  { key: 'stock_asc', label: 'Stok (azdan çoğa)' },
  { key: 'qty_desc', label: 'Satış adedi' },
  { key: 'title_asc', label: 'Ürün adı (A→Z)' },
];

// Ciro yeşil, stok mor — iki metriği gözle ayırmak için ayrı skala
const heat = (n, max, tone) => {
  if (!n) return { background: 'transparent', color: '#d1d5db' };
  const ratio = max > 1 ? Math.min(1, (n - 1) / (max - 1)) : 1;
  const rgb = tone === 'stock' ? '79, 70, 229' : '5, 150, 105';
  return {
    background: `rgba(${rgb}, ${(0.10 + 0.75 * ratio).toFixed(3)})`,
    color: ratio > 0.45 ? '#fff' : tone === 'stock' ? '#312e81' : '#064e3b',
  };
};

export default function High5ProductRevenueStock() {
  const { selectedBrand } = useAuth();
  const [metric, setMetric] = useState('revenue');
  const [rangeKey, setRangeKey] = useState('30');
  const [roll, setRoll] = useState(14);
  const [sort, setSort] = useState('revenue_desc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('ALL');
  const [onlyStock, setOnlyStock] = useState(false);
  const [page, setPage] = useState(0);

  const isRevenue = metric === 'revenue';

  const fetchData = async () => {
    if (!selectedBrand?.id) return;
    try {
      setLoading(true);
      setError('');
      const res = await fetch(
        `${API_BASE}/api/sku-feed/product-revenue-stock?days=${rangeKey}&window=${roll}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': selectedBrand.id },
        }
      );
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d?.error || d?.detail || `HTTP ${res.status}`);
      }
      setPayload(await res.json());
      setPage(0);
    } catch (e) {
      setPayload(null);
      setError(e?.message || 'Veri alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand?.id, rangeKey, roll]);

  const columns = payload?.columns || [];
  const range = payload?.range || {};
  const totals = payload?.totals || null;
  const W = payload?.windowSize || roll;

  const allRows = useMemo(() => payload?.rows || [], [payload]);

  const brands = useMemo(() => {
    const s = new Set();
    allRows.forEach((r) => r.brand && s.add(r.brand));
    return [...s].sort();
  }, [allRows]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out = allRows.filter((r) => {
      if (brandFilter !== 'ALL' && r.brand !== brandFilter) return false;
      if (onlyStock && !r.inMc) return false;
      if (q && !`${r.title || ''} ${r.brand || ''} ${r.itemId || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
    const val = (r) => (r.stockLastDay == null ? -1 : r.stockLastDay);
    const cmp = {
      revenue_desc: (a, b) => b.totalRevenue - a.totalRevenue,
      revenue_asc: (a, b) => a.totalRevenue - b.totalRevenue,
      stock_desc: (a, b) => val(b) - val(a),
      stock_asc: (a, b) => val(a) - val(b),
      qty_desc: (a, b) => b.totalQty - a.totalQty,
      title_asc: (a, b) => String(a.title).localeCompare(String(b.title), 'tr'),
    }[sort];
    return [...out].sort(cmp);
  }, [allRows, search, brandFilter, onlyStock, sort]);

  const paged = rows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));

  // Isı skalası sayfadaki satırlara göre — tek bir devin tabloyu söndürmesini engeller
  const maxCell = useMemo(() => {
    let m = 1;
    paged.forEach((r) => {
      const v = isRevenue ? r.maxCell : r.maxStock;
      if (v > m) m = v;
    });
    return m;
  }, [paged, isRevenue]);

  const exportCsv = () => {
    // CSV'de her gün İKİ kolon: ciro ve beden — sayfadaki hücreyle birebir
    const head = ['Urun', 'Marka', 'Kod', 'Toplam ciro', 'Satis adedi', 'Satisli gun', 'Son gun beden',
      ...columns.flatMap((c) => [`${c.key} ciro`, `${c.key} beden`])];
    const lines = rows.map((r) => {
      const cells = columns.flatMap((c) => [
        r.cells?.[c.key]?.r ?? 0,
        r.stockCells?.[c.key]?.n ?? '',
      ]);
      return [r.title || '', r.brand || '', r.itemId || '', r.totalRevenue, r.totalQty, r.daysSold,
        r.stockLastDay ?? '', ...cells]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',');
    });
    const csv = [head.join(','), ...lines].join('\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `urun-ciro-stok-${metric}_${range.start || ''}_${range.end || ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-6">
      <Card className="rounded-2xl overflow-hidden shadow-xl border border-gray-100">
        {/* Header */}
        <div className="p-6 bg-white border-b">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Ürün Ciro & Stok Takibi</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Satır = ürün, sütun = gün. Her hücrede <strong>üstte o günün cirosu</strong>, altta
                  <strong> o günkü beden sayısı</strong> (<span className="tabular-nums">bd</span>).
                  {selectedBrand?.name ? ` · ${selectedBrand.name}` : ''}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select value={rangeKey} onChange={(e) => setRangeKey(e.target.value)} className="px-3 py-2 bg-white border rounded-xl text-sm">
                {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
              <select value={roll} onChange={(e) => setRoll(Number(e.target.value))} className="px-3 py-2 bg-white border rounded-xl text-sm">
                {ROLLS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
              <button type="button" onClick={exportCsv} disabled={loading || rows.length === 0}
                className="flex items-center gap-2 px-3 h-10 bg-white border rounded-xl text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50" title="CSV indir">
                <Download className="w-4 h-4" /> CSV
              </button>
              <button type="button" onClick={fetchData} disabled={loading}
                className="flex items-center justify-center w-10 h-10 bg-white border rounded-xl text-gray-600 hover:bg-gray-50 disabled:opacity-50" title="Yenile">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Metrik seçici */}
          <div className="mt-5 flex flex-wrap gap-2">
            {METRICS.map((m) => {
              const Icon = m.icon;
              const active = metric === m.key;
              // Tailwind sınıfları statik yazılmalı — `bg-${tone}-600` derlenmez
              const activeCls = m.key === 'stock'
                ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                : 'bg-emerald-600 text-white border-emerald-600 shadow-sm';
              return (
                <button key={m.key} type="button" onClick={() => setMetric(m.key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition ${
                    active ? activeCls : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}>
                  <Icon className="w-4 h-4" />
                  {m.label}
                  <span className={`text-[11px] font-normal ${active ? 'text-white/70' : 'text-gray-400'}`}>· {m.hint}</span>
                </button>
              );
            })}
          </div>

          {/* Özet */}
          <div className="mt-4 rounded-2xl border border-emerald-100 bg-gradient-to-r from-emerald-50 to-white p-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <div className="flex items-center gap-3">
                <CalendarRange className="w-5 h-5 text-emerald-600" />
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-emerald-700/70 font-semibold">Veri aralığı</div>
                  <div className="text-lg font-bold text-gray-900">
                    {fmtDate(range.start)} <span className="text-gray-400 font-normal">→</span> {fmtDate(range.end)}
                  </div>
                </div>
              </div>
              <div className="h-8 w-px bg-emerald-100 hidden sm:block" />
              <div>
                <div className="text-[11px] uppercase tracking-wide text-emerald-700/70 font-semibold">Ürün</div>
                <div className="text-lg font-bold text-gray-900 tabular-nums">
                  {fmtNumber(rows.length)}
                  {payload?.summary && (
                    <span className="text-sm font-normal text-gray-400"> · {fmtNumber(payload.summary.productsWithStock)} tanesinde stok verisi</span>
                  )}
                </div>
              </div>
              {totals && (
                <>
                  <div className="h-8 w-px bg-emerald-100 hidden sm:block" />
                  <div>
                    <div className="text-[11px] uppercase tracking-wide text-emerald-700/70 font-semibold">Toplam ciro</div>
                    <div className="text-lg font-bold text-gray-900 tabular-nums">
                      {fmtMoney(totals.revenue)}
                      <span className="text-sm font-normal text-gray-400"> · {fmtNumber(totals.qty)} adet</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Beden sayısının kaynağı — hücrelerde hep göründüğü için hep yazılı */}
          {(
            <div className="mt-3 flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-900 leading-relaxed">
                “Stok” = o üründe <strong>olan beden sayısı</strong>; gerçek stok ADEDİ hiçbir kaynakta yok.
                Hücreler <strong>iki kaynaklı</strong>:
                <br />
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-sm ring-1 ring-inset ring-emerald-500 bg-indigo-200" />
                  <strong>Yeşil çerçeveli = o günün KESİN sayımı</strong> (hebiar SKU feed'i, her bedeni stok
                  durumuyla verir).
                </span>{' '}
                {payload?.exactDays?.length
                  ? <>Şu an {payload.exactDays.length} günde var: {payload.exactDays.map(fmtDate).join(', ')} — her gün cron'la birikiyor.</>
                  : <>Henüz hiçbir günde yok.</>}
                <br />
                Çerçevesiz hücreler <strong>tahmin</strong>: MC günde ürün başına tek beden verdiği için
                <strong> son {W} günlük pencere</strong> kullanılır. (MC API'si de tek beden dönüyor —
                4 Ağu'da 250 üründe test edildi, kayıp yok.)
                {payload?.reliableFrom && (
                  <> 🔴 <strong>{fmtDate(payload.reliableFrom)} öncesi tahminler güvenilmez:</strong> feed o
                  tarihe kadar her gün aynı bedeni tekrarlıyordu, pencere ~1 beden görüyordu.</>
                )}
                {' '}Boş hücre = ürün o gün hiçbir kaynakta yoktu (ciro olabilir, stok verisi yok).
              </div>
            </div>
          )}
          <div className="mt-3 flex gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
            <Info className="w-4 h-4 text-gray-500 shrink-0 mt-0.5" />
            <div className="text-xs text-gray-600 leading-relaxed">
              Ciro kaynağı <strong>GA4 item satışları</strong> (günlük, ürün bazında). Hücrede üstteki
              kalın sayı o günün cirosu, altındaki “<span className="tabular-nums">bd</span>” o günkü beden
              sayısıdır. Üstteki düğmeler sadece <strong>renklendirmeyi</strong> değiştirir, iki sayı da
              her zaman yazar. Sıralamayı aşağıdaki “Sırala” kutusundan yapabilirsin.
            </div>
          </div>
        </div>

        {/* Filtreler */}
        <div className="p-4 bg-gray-50/60 border-b flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder="Ürün adı, marka veya kod ara…"
              className="w-full pl-9 pr-3 py-2 bg-white border rounded-xl text-sm"
            />
          </div>
          <select value={brandFilter} onChange={(e) => { setBrandFilter(e.target.value); setPage(0); }}
            className="px-3 py-2 bg-white border rounded-xl text-sm">
            <option value="ALL">Tüm markalar</option>
            {brands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select value={sort} onChange={(e) => { setSort(e.target.value); setPage(0); }}
            className="px-3 py-2 bg-white border rounded-xl text-sm" title="Sırala">
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
            <input type="checkbox" checked={onlyStock} onChange={(e) => { setOnlyStock(e.target.checked); setPage(0); }} />
            Sadece stok verisi olanlar
          </label>
        </div>

        {/* Matris */}
        <div className="p-0">
          {error && <div className="m-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}
          {!selectedBrand?.id && <div className="p-6 text-center text-gray-500">Lütfen bir marka seçin.</div>}
          {loading && <div className="p-8 text-center text-gray-400">Yükleniyor…</div>}
          {!loading && !error && paged.length === 0 && selectedBrand?.id && (
            <div className="p-8 text-center text-gray-400">Kayıt yok.</div>
          )}

          {!loading && !error && paged.length > 0 && (
            <div className="overflow-auto max-h-[70vh]">
              <table className="text-sm border-separate" style={{ borderSpacing: 0 }}>
                <thead>
                  <tr>
                    <th className="sticky left-0 top-0 z-30 bg-gray-50 border-b border-r border-gray-200 px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[280px]">
                      Ürün
                    </th>
                    <th className="sticky top-0 z-20 bg-gray-50 border-b border-gray-200 px-2 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[104px]"
                        style={{ left: 280 }} title="Seçili aralıktaki toplam ciro">
                      Toplam ciro
                    </th>
                    <th className="sticky top-0 z-20 bg-gray-50 border-b border-gray-200 px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[56px]"
                        style={{ left: 384 }} title="Seçili aralıkta satılan adet">
                      Adet
                    </th>
                    <th className="sticky top-0 z-20 bg-gray-50 border-b border-r border-gray-200 px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide min-w-[60px]"
                        style={{ left: 440 }} title={`Son gün, son ${W} günde görülen farklı beden sayısı`}>
                      Stok
                    </th>
                    {columns.map((c) => (
                      <th key={c.key} title={fmtDate(c.key)}
                          className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200 px-2 py-2 text-center text-[11px] font-semibold text-gray-500 whitespace-nowrap min-w-[58px]">
                        {colLabel(c.key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* TOPLAM — gün gün toplam ciro */}
                  {totals && (
                    <tr className="font-semibold">
                      <td className="sticky left-0 z-20 bg-emerald-50 border-b border-r border-emerald-200 px-3 py-2 min-w-[280px]">
                        <div className="text-gray-900">TOPLAM · günlük ciro</div>
                        <div className="text-[10px] text-gray-500 font-normal">
                          {fmtNumber(payload.summary?.products || 0)} ürün · {fmtNumber(totals.qty)} adet
                        </div>
                      </td>
                      <td className="sticky z-10 bg-emerald-50 border-b border-emerald-200 px-2 py-2 text-right tabular-nums text-gray-900" style={{ left: 280 }}>
                        {fmtMoney(totals.revenue)}
                      </td>
                      <td className="sticky z-10 bg-emerald-50 border-b border-emerald-200 px-2 py-2 text-center tabular-nums text-gray-900" style={{ left: 384 }}>
                        {fmtNumber(totals.qty)}
                      </td>
                      <td className="sticky z-10 bg-emerald-50 border-b border-r border-emerald-200 px-2 py-2 text-center text-gray-400" style={{ left: 440 }}>—</td>
                      {columns.map((c) => {
                        const cell = totals.cells?.[c.key];
                        const r = cell?.r || 0;
                        return (
                          <td key={c.key}
                              title={`${fmtDate(c.key)} · ${fmtMoney(r)} · ${fmtNumber(cell?.q || 0)} adet · ${fmtNumber(cell?.s || 0)} beden`}
                              className="border-b border-emerald-200 px-1 py-1 text-center tabular-nums cursor-default"
                              style={heat(isRevenue ? r : (cell?.s || 0), (isRevenue ? totals.maxCell : totals.maxStock) || 1, isRevenue ? 'revenue' : 'stock')}>
                            <div className="leading-none text-xs font-bold">{fmtCompact(r)}</div>
                            <div className="leading-none text-[10px] font-medium opacity-75 mt-0.5">
                              {fmtCompact(cell?.s || 0)} bd
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  )}
                  {paged.map((r) => (
                    <tr key={r.itemId} className="hover:bg-emerald-50/20">
                      <td className="sticky left-0 z-20 bg-white border-b border-r border-gray-100 px-3 py-1.5 min-w-[280px]">
                        <div className="flex items-center gap-2">
                          {r.image ? (
                            <img src={r.image} alt="" loading="lazy" className="w-8 h-8 rounded object-cover border border-gray-100 bg-white shrink-0" />
                          ) : <div className="w-8 h-8 rounded bg-gray-100 shrink-0" />}
                          <div className="min-w-0">
                            <div className="font-medium text-gray-800 truncate max-w-[210px] text-xs" title={r.title || ''}>
                              {r.title}
                            </div>
                            <div className="text-[10px] text-gray-400 truncate max-w-[210px]">
                              {r.brand}{r.brand && r.itemId ? ' · ' : ''}{r.itemId}
                              {!r.inMc && <span className="text-amber-600"> · MC'de yok</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="sticky z-10 bg-white border-b border-gray-100 px-2 py-1.5 text-right tabular-nums text-xs font-semibold text-emerald-700" style={{ left: 280 }}>
                        {fmtMoney(r.totalRevenue)}
                      </td>
                      <td className="sticky z-10 bg-white border-b border-gray-100 px-2 py-1.5 text-center tabular-nums text-xs text-gray-600" style={{ left: 384 }}>
                        {fmtNumber(r.totalQty)}
                      </td>
                      <td className="sticky z-10 bg-white border-b border-r border-gray-100 px-2 py-1.5 text-center" style={{ left: 440 }}>
                        {r.stockLastDay == null
                          ? <span className="text-gray-300">—</span>
                          : <span className={`inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full font-bold text-xs ${
                              r.stockLastDay === 0 ? 'bg-red-100 text-red-700' : 'bg-indigo-100 text-indigo-800'
                            }`}>{r.stockLastDay}</span>}
                      </td>
                      {columns.map((c) => {
                        const rev = r.cells?.[c.key];
                        const stock = r.stockCells?.[c.key];
                        const exact = !!stock?.e;
                        // Kesin sayım varken rotasyon uyarısı geçersiz — o hücre tahmin değil
                        const stale = !isRevenue && !exact && payload?.reliableFrom && c.key < payload.reliableFrom;
                        const val = isRevenue ? (rev?.r || 0) : (stock?.n ?? 0);
                        const tip = `${fmtDate(c.key)} · ${fmtMoney(rev?.r || 0)} · ${fmtNumber(rev?.q || 0)} adet`
                          + (stock != null
                              ? ` · ${stock.n} beden ${exact ? '(kesin sayım — hebiar)' : `(tahmin — ${W}g MC penceresi)`}`
                              : ' · stok verisi yok')
                          + (stale ? ' ⚠️ feed o tarihte beden döndürmüyordu' : '');
                        return (
                          <td key={c.key} title={tip}
                              className={`border-b border-gray-100 px-1 py-1 text-center tabular-nums cursor-default${stale ? ' opacity-50' : ''}${exact ? ' ring-1 ring-inset ring-emerald-500' : ''}`}
                              style={heat(val, maxCell, isRevenue ? 'revenue' : 'stock')}>
                            {/* Her hücrede İKİSİ birden: üstte o günün cirosu, altta o günün beden sayısı */}
                            <div className="leading-none text-xs font-bold">
                              {rev ? fmtCompact(rev.r) : <span className="opacity-40">·</span>}
                            </div>
                            <div className="leading-none text-[10px] font-medium opacity-75 mt-0.5">
                              {stock != null ? `${stock.n} bd` : '–'}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Sayfalama */}
        {!loading && rows.length > PAGE_SIZE && (
          <div className="p-4 border-t bg-white flex items-center justify-between text-sm text-gray-600">
            <div>{fmtNumber(rows.length)} üründen {page * PAGE_SIZE + 1}–{Math.min(rows.length, (page + 1) * PAGE_SIZE)} arası</div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                className="flex items-center justify-center w-9 h-9 bg-white border rounded-lg disabled:opacity-40">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="tabular-nums">{page + 1} / {pageCount}</span>
              <button type="button" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1}
                className="flex items-center justify-center w-9 h-9 bg-white border rounded-lg disabled:opacity-40">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
